import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { TrackingKeywordSuggestion, KeywordSuggestionFilter, KeywordSuggestion, PageSuggestion, SaveActions, KeywordSuggestionSources } from '../shared/models/keywords/keywords';

@Injectable({
  providedIn: 'root'
})
export class KeywordResearchService {
  private baseUrl = `${environment.urlConfig.serviceUrl}/keywordresearch`;

  constructor(private http: HttpClient) { }

  getTrackingKeywordSuggestions(customerId: number): Observable<TrackingKeywordSuggestion[]> {
    const params = new HttpParams().set('customerId', customerId);

    return this.http.get<TrackingKeywordSuggestion[]>(`${this.baseUrl}/trackingKeywordSuggestions`, { params: params })
  }

  getPageSuggestions(customerId: number): Observable<PageSuggestion[]> {
    const params = new HttpParams().set('customerId', customerId);

    return this.http.get<PageSuggestion[]>(`${this.baseUrl}/pageSuggestions`, { params: params })
  }

  save(customerId: number, pageSuggestions: PageSuggestion[], trackingKeywordSuggestions: TrackingKeywordSuggestion[], saveActions: SaveActions[], requestSource: Boo.Objects.Enums.RequestSources, isCampaignNational?: boolean): Observable<any> {
    return this.http.post(`${this.baseUrl}/save`, {
      customerId: customerId,
      pageSuggestions: pageSuggestions,
      trackingKeywordSuggestions: trackingKeywordSuggestions,
      saveActions: saveActions,
      requestSource: requestSource,
      isCampaignNational: isCampaignNational
    });
  }

  requestNewPageSuggestions(customerId: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/requestNewPageSuggestions`, customerId);
  }

  getExpectedCompletionMinutes(): Observable<number> {
    return this.http.get<number>(`${this.baseUrl}/expectedCompletionMinutes`);
  }

  keywordResearchInProgress(customerId: number, requestSource: Boo.Objects.Enums.RequestSources): Observable<boolean> {
    const params = new HttpParams().set('customerId', customerId).set('requestSource', requestSource);

    return this.http.get<boolean>(`${this.baseUrl}/keywordResearchInProgress`, { params: params });
  }

  defaultKeywordSuggestionFilter: KeywordSuggestionFilter = {
    keyword: '',
    fromRank: 1,
    toRank: 101,
    competition: 'Any'
  } as KeywordSuggestionFilter;

  filterKeywordSuggestions(keywordSuggestion: KeywordSuggestion[], keywordSuggestionFilter: KeywordSuggestionFilter): KeywordSuggestion[] {
    if (!keywordSuggestionFilter) {
      return keywordSuggestion;
    }

    const fromRank = keywordSuggestionFilter.fromRank ?? this.defaultKeywordSuggestionFilter.fromRank;
    const toRank = keywordSuggestionFilter.toRank ?? this.defaultKeywordSuggestionFilter.toRank;
    const includeUnranked = fromRank === this.defaultKeywordSuggestionFilter.fromRank && toRank === this.defaultKeywordSuggestionFilter.toRank;

    return keywordSuggestion.filter(x => x.KeywordPhrase.toLowerCase().includes(keywordSuggestionFilter.keyword)
      && (x.Rank === null ? includeUnranked : (x.Rank >= fromRank && x.Rank <= toRank))
      && (keywordSuggestionFilter.competition === this.defaultKeywordSuggestionFilter.competition || x.CompetitionDescription === keywordSuggestionFilter.competition));
  }

  filterTrackingKeywordSuggestions(trackingKeywordSuggestions: TrackingKeywordSuggestion[], keywordSuggestionFilter: KeywordSuggestionFilter): TrackingKeywordSuggestion[] {
    if (!keywordSuggestionFilter) {
      return trackingKeywordSuggestions;
    }

    const fromRank = keywordSuggestionFilter.fromRank ?? this.defaultKeywordSuggestionFilter.fromRank;
    const toRank = keywordSuggestionFilter.toRank ?? this.defaultKeywordSuggestionFilter.toRank;
    const includeUnranked = fromRank === this.defaultKeywordSuggestionFilter.fromRank && toRank === this.defaultKeywordSuggestionFilter.toRank;

    return trackingKeywordSuggestions.filter(x => x.KeywordPhrase.toLowerCase().includes(keywordSuggestionFilter.keyword)
      && (x.Rank === null ? includeUnranked : (x.Rank >= fromRank && x.Rank <= toRank))
      && (keywordSuggestionFilter.competition === this.defaultKeywordSuggestionFilter.competition || x.CompetitionDescription === keywordSuggestionFilter.competition));
  }

  pageSort = (a: PageSuggestion, b: PageSuggestion): number => {
    if (!a.Url && b.Url) {
      return 1;
    }
    if (a.Url && !b.Url) {
        return -1;
    }
    return a.Url.toLowerCase().localeCompare(b.Url.toLowerCase());
  }

  rankThreshold = 3;

  keywordSort = (a: KeywordSuggestion, b: KeywordSuggestion): number => {
    if (a.Rank === null && b.Rank !== null) {
      return 1;
    }
    if (a.Rank !== null && b.Rank === null) {
      return -1;
    }

    if (a.Rank <= this.rankThreshold && b.Rank > this.rankThreshold) {
      return 1;
    }
    if (a.Rank > this.rankThreshold && b.Rank <= this.rankThreshold) {
      return -1;
    }

    if (a.SourceId === KeywordSuggestionSources.CustomerCampaign && b.SourceId !== KeywordSuggestionSources.CustomerCampaign) {
      return 1;
    }
    if (a.SourceId !== KeywordSuggestionSources.CustomerCampaign && b.SourceId === KeywordSuggestionSources.CustomerCampaign) {
      return -1;
    }

    if (a.SourceId === KeywordSuggestionSources.User && b.SourceId !== KeywordSuggestionSources.User) {
      return 1;
    }
    if (a.SourceId !== KeywordSuggestionSources.User && b.SourceId === KeywordSuggestionSources.User) {
      return -1;
    }

    if (a.Volume === null && b.Volume !== null) {
      return 1;
    }
    if (a.Volume !== null && b.Volume === null) {
      return -1;
    }
    if (a.Volume !== b.Volume) {
      return b.Volume - a.Volume; // desc
    }

    if (a.Competition === null && b.Competition !== null) {
      return 1;
    }
    if (a.Competition !== null && b.Competition === null) {
      return -1;
    }
    if (a.Competition !== b.Competition) {
      return a.Competition - b.Competition; // asc
    }

    return a.KeywordPhrase.toLowerCase().localeCompare(b.KeywordPhrase.toLowerCase());
  }
}
